import axios, { AxiosInstance, AxiosError, AxiosRequestConfig } from "axios";

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  
  failedQueue = [];
};

export const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_BASE_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // If the data is FormData, remove the Content-Type header
    // to let axios set it automatically with the proper boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Skip retry for auth endpoints
      if (originalRequest.url?.includes('/login') || 
          originalRequest.url?.includes('/refresh-token') ||
          originalRequest.url?.includes('/logout')) {
        return Promise.reject(error);
      }
      
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers!.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        }).catch((err) => {
          return Promise.reject(err);
        });
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      
      const refreshToken = localStorage.getItem('refresh_token');
      
      if (!refreshToken) {
        // No refresh token available
        processQueue(error, null);
        isRefreshing = false;
        // Don't redirect here, let the components handle it
        return Promise.reject(error);
      }
      
      try {
        // Import auth store dynamically to avoid circular dependency
        const { useAuthStore } = await import('../store/authStore');
        const authStore = useAuthStore.getState();
        const success = await authStore.refreshAccessToken();
        
        if (success) {
          const newToken = localStorage.getItem('token');
          processQueue(null, newToken);
          originalRequest.headers!.Authorization = `Bearer ${newToken}`;
          return apiClient(originalRequest);
        } else {
          // Refresh failed, clear state and redirect to login
          processQueue(error, null);
          
          // Clear auth state directly without calling logout API
          localStorage.removeItem('token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('auth-storage');
          
          // Reset store state using setState
          useAuthStore.setState({
            token: null,
            refreshToken: null,
            user: null,
            isAuthenticated: false,
            error: 'Session expired. Please log in again.'
          });
          
          // Redirect to login
          if (!window.location.pathname.includes("/login")) {
            window.location.href = "/login";
          }
          
          return Promise.reject(error);
        }
      } catch (refreshError) {
        processQueue(refreshError instanceof Error ? refreshError : new Error(String(refreshError)), null);
        
        // Import auth store to clear state
        const { useAuthStore } = await import('../store/authStore');
        
        // Clear auth state directly without calling logout API
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('auth-storage');
        
        // Reset store state using setState
        useAuthStore.setState({
          token: null,
          refreshToken: null,
          user: null,
          isAuthenticated: false,
          error: 'Session expired. Please log in again.'
        });
        
        // Redirect to login
        if (!window.location.pathname.includes("/login")) {
          window.location.href = "/login";
        }
        
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    
    return Promise.reject(error);
  }
);

export const requiresAuth = async <T>(callback: () => Promise<T>): Promise<T> => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Authentication required");
  }
  return callback();
};

// Export api as an alias for apiClient for backward compatibility
export const api = apiClient;
